import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaginationInformation } from '../types/api';
import { PaginationOptions } from '../utils/pagination';

export type UsePaginationResult<TData> = {
  data: TData[];
  page: number;
  /** Jump to an absolute page, clamped to `[1, totalPages]`. */
  setPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pagination: Pick<PaginationInformation, 'total' | 'total_pages' | 'per_page'> | null;
  setPageSize: (size: number) => void;
  pageSizeOptions?: number[];
};

export function usePagination<TData>(
  data: TData[] | undefined,
  options?: {
    pageSize?: number;
    pageSizeOptions?: number[];
  },
): UsePaginationResult<TData> {
  const [page, setPage] = useState(PaginationOptions.INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(
    options?.pageSize ?? PaginationOptions.DEFAULT_PAGE_SIZE,
  );

  const total = data?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Keyed on `total`, not `data` — a filtered array is a new reference every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on total/pageSize change
  useEffect(() => {
    setPage(PaginationOptions.INITIAL_PAGE);
  }, [total, pageSize]);

  const goToPage = useCallback(
    (next: number) => setPage(Math.min(Math.max(next, 1), totalPages)),
    [totalPages],
  );
  const nextPage = useCallback(() => setPage((p) => (p < totalPages ? p + 1 : p)), [totalPages]);
  const previousPage = useCallback(() => setPage((p) => (p > 1 ? p - 1 : p)), []);

  const pageData = useMemo(
    () => (data ?? []).slice((page - 1) * pageSize, page * pageSize),
    [data, page, pageSize],
  );

  const pagination = useMemo<UsePaginationResult<TData>['pagination']>(
    () => (data ? { total, total_pages: totalPages, per_page: pageSize } : null),
    [data, total, totalPages, pageSize],
  );

  return {
    data: pageData,
    page,
    setPage: goToPage,
    nextPage,
    previousPage,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
    pagination,
    setPageSize,
    pageSizeOptions: options?.pageSizeOptions,
  };
}
